import { Link } from "react-router-dom";
import * as Icons from "lucide-react";
import { Reveal, SectionLabel } from "../components/Reveal";
import BrandsSection from "../components/BrandsSection";
import { IMG } from "../data/content";
import { useSiteServices } from "../hooks/useSiteServices";

function ServiceBlock({ offering, index }) {
  const Icon = Icons[offering.icon] || Icons.Sparkles;
  return (
    <Reveal delay={index * 0.04}>
      <article
        data-testid={`service-offering-${index}`}
        className="rounded-3xl border border-border bg-card overflow-hidden"
      >
        <div className="p-8 md:p-10 border-b border-border/60 bg-secondary/20">
          <div className="flex flex-col sm:flex-row sm:items-start gap-6">
            <span className="grid place-items-center w-16 h-16 rounded-2xl bg-primary text-primary-foreground shrink-0 shadow-[0_8px_24px_rgba(44,76,59,0.2)]">
              <Icon size={30} strokeWidth={1.5} />
            </span>
            <div>
              <h2 className="font-serif-display text-3xl md:text-4xl leading-tight">{offering.title}</h2>
              <p className="mt-4 text-muted-foreground leading-relaxed max-w-3xl">{offering.summary}</p>
            </div>
          </div>
        </div>
        <div className="p-8 md:p-10 space-y-8">
          {offering.groups.map((group, gi) => (
            <div key={gi}>
              {group.title && (
                <h3 className="label-caps text-accent mb-4">{group.title}</h3>
              )}
              {!group.title && offering.groups.length === 1 && (
                <h3 className="label-caps text-muted-foreground mb-4">Services Include</h3>
              )}
              <ul className="grid sm:grid-cols-2 lg:grid-cols-3 gap-x-6 gap-y-3">
                {group.items.map((item) => (
                  <li key={item} className="flex items-start gap-2.5 text-sm md:text-base text-foreground/90">
                    <Icons.Check size={18} className="text-accent mt-0.5 shrink-0" />
                    <span>{item}</span>
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>
      </article>
    </Reveal>
  );
}

export default function Services() {
  const { offerings, industries, why_choose: whyChoose, packages } = useSiteServices();

  return (
    <div className="pt-32 pb-8">
      <section className="max-w-7xl mx-auto px-6">
        <Reveal><SectionLabel>Our Services</SectionLabel></Reveal>
        <Reveal delay={0.1}>
          <h1 className="font-serif-display text-5xl md:text-6xl mt-6 max-w-4xl leading-tight">
            Complete solutions from design to delivery—and beyond.
          </h1>
        </Reveal>
        <Reveal delay={0.2}>
          <p className="mt-8 max-w-3xl text-lg text-muted-foreground leading-relaxed">
            Kalpavriksha brings interiors, construction, real estate, maintenance, manufacturing, and
            design under one roof—so you work with a single trusted partner from first sketch to
            long-term care.
          </p>
        </Reveal>
      </section>

      <section className="max-w-7xl mx-auto px-6 py-16 md:py-20 space-y-8">
        {offerings.map((offering, i) => (
          <ServiceBlock key={offering.title} offering={offering} index={i} />
        ))}
      </section>

      <section className="bg-secondary/40 border-y border-border">
        <div className="max-w-7xl mx-auto px-6 py-20">
          <Reveal><SectionLabel>Industries We Serve</SectionLabel></Reveal>
          <Reveal delay={0.08}>
            <h2 className="font-serif-display text-4xl md:text-5xl mt-5 mb-4">
              Customized solutions across sectors
            </h2>
            <p className="text-muted-foreground max-w-2xl mb-12">
              We adapt our process, materials, and timelines to the standards each industry demands.
            </p>
          </Reveal>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {industries.map((name, i) => (
              <Reveal key={name} delay={i * 0.04}>
                <div className="flex items-center gap-3 rounded-2xl border border-border bg-card px-5 py-4">
                  <span className="grid place-items-center w-10 h-10 rounded-xl bg-primary/10 text-primary shrink-0">
                    <Icons.Briefcase size={20} />
                  </span>
                  <span className="font-medium">{name}</span>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <section className="max-w-7xl mx-auto px-6 py-20">
        <Reveal><SectionLabel>Why Choose Kalpavriksha</SectionLabel></Reveal>
        <Reveal delay={0.08}>
          <h2 className="font-serif-display text-4xl md:text-5xl mt-5 mb-14">Built on trust, delivered with care</h2>
        </Reveal>
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
          {whyChoose.map((item, i) => {
            const Icon = Icons[item.icon] || Icons.Star;
            return (
              <Reveal key={item.title} delay={i * 0.05}>
                <div className="hover-lift h-full rounded-2xl border border-border bg-card p-8">
                  <span className="grid place-items-center w-12 h-12 rounded-xl bg-accent/15 text-accent mb-5">
                    <Icon size={24} />
                  </span>
                  <h3 className="font-serif-display text-xl">{item.title}</h3>
                  <p className="mt-3 text-muted-foreground leading-relaxed text-sm">{item.desc}</p>
                </div>
              </Reveal>
            );
          })}
        </div>
      </section>

      <section className="bg-secondary/40 border-y border-border">
        <div className="max-w-7xl mx-auto px-6 py-24">
          <Reveal><SectionLabel>Package Rates</SectionLabel></Reveal>
          <h2 className="font-serif-display text-4xl md:text-5xl mt-5 mb-14">Interior package pricing</h2>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {packages.map((p, i) => (
              <Reveal key={p.name} delay={i * 0.07}>
                <div className={`h-full rounded-2xl border p-8 flex flex-col ${p.name === "Premium" ? "bg-primary text-primary-foreground border-accent" : "bg-card border-border"}`}>
                  <p className="label-caps text-accent">{p.tag}</p>
                  <h3 className="font-serif-display text-3xl mt-2">{p.name}</h3>
                  <p className="font-serif-display text-4xl mt-4 flex items-baseline gap-2 flex-wrap" data-testid={`services-pkg-price-${p.name.toLowerCase()}`}>
                    {p.oldRate && <span className="text-2xl line-through opacity-40">₹{p.oldRate.toLocaleString("en-IN")}</span>}
                    <span>₹{p.rate.toLocaleString("en-IN")}<span className="text-base font-sans opacity-60"> /sq.ft</span></span>
                  </p>
                  <ul className="mt-6 space-y-3 flex-1">
                    {p.features.map((f) => (
                      <li key={f} className="flex items-start gap-2 text-sm">
                        <Icons.Check size={16} className="text-accent mt-0.5 shrink-0" /> {f}
                      </li>
                    ))}
                  </ul>
                  <Link to="/calculator" className={`mt-8 text-center rounded-full py-3 font-medium transition-colors ${p.name === "Premium" ? "bg-accent text-accent-foreground" : "bg-primary text-primary-foreground hover:bg-accent hover:text-accent-foreground"}`}>
                    Estimate
                  </Link>
                </div>
              </Reveal>
            ))}
          </div>
          <p className="mt-8 text-sm text-muted-foreground max-w-2xl">
            Rates apply to interior packages. Additional charges may apply for civil modifications,
            electrical upgrades, false ceiling customization, automation and premium appliances.
            Final quotation confirmed after a site visit.
          </p>
        </div>
      </section>

      <BrandsSection />

      <section className="max-w-7xl mx-auto px-6 py-24 grid lg:grid-cols-2 gap-14 items-center">
        <Reveal><img src={IMG.architecture} alt="Construction" className="rounded-3xl w-full object-cover aspect-[4/3]" /></Reveal>
        <Reveal delay={0.15}>
          <SectionLabel>One partner, every phase</SectionLabel>
          <h2 className="font-serif-display text-4xl md:text-5xl mt-5 leading-tight">From blueprint to keys—and after</h2>
          <p className="mt-6 text-muted-foreground leading-relaxed">
            Whether you need a turnkey home, a commercial fit-out, property advisory, or long-term
            maintenance, our teams coordinate under the Kalpavriksha name so nothing falls through
            the cracks.
          </p>
          <Link
            to="/contact"
            className="inline-flex mt-8 rounded-full bg-primary text-primary-foreground px-8 py-3 font-medium hover:bg-accent hover:text-accent-foreground transition-colors"
          >
            Talk to our team
          </Link>
        </Reveal>
      </section>
    </div>
  );
}
